import React, { useEffect, useState, useMemo, useContext } from "react"
import Breadcrumb from "components/Common/Breadcrumb"
import {
  Col,
  Container,
  Row,
  Table,
  Button,
  Input,
  Dropdown,
  DropdownToggle,
  DropdownMenu,
  DropdownItem,
} from "reactstrap"
import Select from "react-select"
import Swal from "sweetalert2"
import * as XLSX from "xlsx"
import jsPDF from "jspdf"
import autoTable from "jspdf-autotable"
import { examMasterApi } from "apis/ExamMasterApi"
import { sessionApi } from "apis/SessionApi"
import { studentMarkEntries } from "apis/MarkEntriesApi"
import { Link } from "react-router-dom"
import { SessionContext } from "context/sessionContext"

const selectStyles = {
  menu: provided => ({ ...provided, backgroundColor: "#fff", zIndex: 1000 }),
  menuList: provided => ({
    ...provided,
    backgroundColor: "#fff",
    maxHeight: "200px",
  }),
  option: (provided, state) => ({
    ...provided,
    backgroundColor: state.isFocused ? "#f8f9fa" : "#fff",
    color: "#000",
    cursor: "pointer",
  }),
}

const ATTEMPT_OPTIONS = [
  { value: 1, label: "Attempt 1" },
  { value: 2, label: "Attempt 2" },
  { value: 3, label: "Attempt 3" },
  { value: 4, label: "Attempt 4" },
]

// ── Pivot helper: API returns one row per (student, subject, topic) ───────
const pivotEntries = entries => {
  const studentMap = new Map()
  entries.forEach(entry => {
    const key = entry.st_code
    if (!studentMap.has(key)) {
      studentMap.set(key, {
        id: key,
        roll_no: entry.st_code,
        student_name: entry.st_name,
        subjects: {},
      })
    }
    const student = studentMap.get(key)
    const columnLabel = entry.topic_name
      ? `${entry.subject_name} - ${entry.topic_name}`
      : entry.subject_name
    student.subjects[columnLabel] =
      entry.mark_value !== null && entry.mark_value !== undefined
        ? entry.mark_value
        : entry.grade_value !== null && entry.grade_value !== undefined
        ? entry.grade_value
        : "—"
  })
  return Array.from(studentMap.values())
}

const getSubjectColumns = data => {
  const seen = new Set()
  data.forEach(row => Object.keys(row.subjects).forEach(s => seen.add(s)))
  return [...seen]
}

// Derive unique category options from a subjects array
const buildCategoryOptions = subjects => {
  const seen = new Map()
  subjects.forEach(s => {
    if (!seen.has(s.category_id)) {
      seen.set(s.category_id, {
        value: s.category_id,
        label: s.category_name,
      })
    }
  })
  return Array.from(seen.values())
}

// Build subject options from selected assessment + category (mirrors MarkEntries)
const buildSubjectOptions = (assessmentOption, categoryId) => {
  if (!assessmentOption || !categoryId) return []
  const subjects = assessmentOption.subjects || []
  return subjects
    .filter(s => s.category_id === categoryId)
    .map(s => ({
      value: s.id,
      label: s.topic_name
        ? `${s.subject_name} — ${s.topic_name}`
        : s.subject_name,
      subjectData: s,
    }))
}



// st_class on each subject is an array of "class section" strings e.g. ["xii D2", "xii A"]
// Build dropdown options showing full "class section", deduped across all matching subjects
const buildClassOptions = (subjects, categoryId) => {
  const seen = new Set()
  subjects
    .filter(s => s.category_id === categoryId)
    .forEach(s => {
      const classes = Array.isArray(s.st_class) ? s.st_class : []
      classes.forEach(cls => {
        const trimmed = String(cls).trim()
        if (trimmed) seen.add(trimmed)
      })
    })
  return Array.from(seen).map(cls => {
    const parts = cls.split(" ")
    const st_class = parts[0] || cls
    const st_sec = parts.slice(1).join(" ") || ""
    return {
      value: cls,
      label: cls.toUpperCase(),
      st_class,
      st_sec,
    }
  })
}

const StudentMarksReport = () => {
  document.title = "Mark Entry & Report"
  // ── Filter state ──────────────────────────────────────────────────────
  const [selectedExam, setSelectedExam] = useState(null)
  const [selectedAssessment, setSelectedAssessment] = useState(null)
  const [selectedCategory, setSelectedCategory] = useState(null)
  const [selectedClass, setSelectedClass] = useState(null)
  const [selectedAttempt, setSelectedAttempt] = useState(null)
  const [selectedSubject, setSelectedSubject] = useState(null)

  // ── Options ───────────────────────────────────────────────────────────
  const [examOptions, setExamOptions] = useState([])
  const [assessmentOptions, setAssessmentOptions] = useState([])
  const [categoryOptions, setCategoryOptions] = useState([])
  const [classList, setClassList] = useState([])
  const [subjectOptions, setSubjectOptions] = useState([])
  const [examsData, setExamsData] = useState([])
  // subjects belonging to the currently selected assessment / sub-assessment level
  const [selectedLevelSubjects, setSelectedLevelSubjects] = useState([])

  const [exportDropdownOpen, setExportDropdownOpen] = useState(false)

  // ── Session ───────────────────────────────────────────────────────────
  const { Session } = useContext(SessionContext)
  const [fallbackSessionId, setFallbackSessionId] = useState(null)
  const activeSessionId = Session || sessionStorage.getItem("SessionId") || fallbackSessionId

  // ── Report state ──────────────────────────────────────────────────────
  const [rawEntries, setRawEntries] = useState([])
  const [hasGenerated, setHasGenerated] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const [isGenerating, setIsGenerating] = useState(false)

  // ── Search ────────────────────────────────────────────────────────────
  const [searchQuery, setSearchQuery] = useState("")
  const [appliedSearch, setAppliedSearch] = useState("")

  // ── Pagination ────────────────────────────────────────────────────────
  const [currentPage, setCurrentPage] = useState(1)
  const [totalPages, setTotalPages] = useState(1)
  const [totalCount, setTotalCount] = useState(0)

  const [examsPage, setExamsPage] = useState(1)
  const [examsTotalPages, setExamsTotalPages] = useState(1)
  const [examsLoadingMore, setExamsLoadingMore] = useState(false)

  // ── Fetch fallback active session silently if none is set in context/storage ─────────────────────────────────────
  useEffect(() => {
    const fetchSession = async () => {
      if (Session || sessionStorage.getItem("SessionId")) return
      try {
        const res = await sessionApi.GetSession()
        if (res?.data?.session && Array.isArray(res.data.session)) {
          const active = res.data.session.find(s => s.is_active)
          if (active) setFallbackSessionId(active.id)
          else if (res.data.session.length > 0)
            setFallbackSessionId(res.data.session[0].id)
        }
      } catch (err) {
        console.error("Failed to fetch sessions:", err.message)
      }
    }
    fetchSession()
  }, [Session])

  // ── Fetch exams ───────────────────────────────────────────────────────
  const fetchExams = async (page = 1) => {
    try {
      if (page === 1) {
        // initial load — no special loading flag needed, options just start empty
      } else {
        setExamsLoadingMore(true)
      }

      const response = await examMasterApi.getExam(page, "", activeSessionId ? parseInt(activeSessionId, 10) : null)
      const allExams = response?.data?.exams || []
      const exams = allExams
      const totalPages = response?.data?.total_pages ?? 1

      setExamsData(prev => (page === 1 ? exams : [...prev, ...exams]))
      setExamOptions(prev =>
        page === 1
          ? exams.map(e => ({ value: e.id, label: e.exam_name }))
          : [...prev, ...exams.map(e => ({ value: e.id, label: e.exam_name }))]
      )
      setExamsTotalPages(totalPages)
      setExamsPage(page)
    } catch {
      Swal.fire("Error", "Failed to fetch exams.", "error")
    } finally {
      setExamsLoadingMore(false)
    }
  }

  useEffect(() => {
    if (activeSessionId) {
      setSelectedExam(null)
      setSelectedAssessment(null)
      setSelectedCategory(null)
      setSelectedClass(null)
      setSelectedAttempt(null)
      setSelectedSubject(null)
      setExamOptions([])
      setAssessmentOptions([])
      setCategoryOptions([])
      setClassList([])
      setSubjectOptions([])
      setSelectedLevelSubjects([])
      resetReportState()
      fetchExams(1)
    }
  }, [activeSessionId])

  const handleExamMenuScrollToBottom = () => {
    if (!examsLoadingMore && examsPage < examsTotalPages) {
      fetchExams(examsPage + 1)
    }
  }
  // ── Cascade resets ────────────────────────────────────────────────────
  const resetReportState = () => {
    setHasGenerated(false)
    setRawEntries([])
    setCurrentPage(1)
    setTotalPages(1)
    setTotalCount(0)
  }

  // ── Exam change ───────────────────────────────────────────────────────
  const handleExamChange = option => {
    setSelectedExam(option)
    setSelectedAssessment(null)
    setSelectedCategory(null)
    setSelectedClass(null)
    setSelectedAttempt(null)
    setSelectedSubject(null)
    setCategoryOptions([])
    setClassList([])
    setSubjectOptions([])
    setSelectedLevelSubjects([])
    resetReportState()

    if (option) {
      const matched = examsData.find(e => e.id === option.value)
      const assessments = matched?.assessments || []
      // Build assessment dropdown: if an assessment has sub_assessments, expand them;
      // otherwise use the assessment itself
      const options = []
      assessments.forEach(a => {
        if (a.sub_assessments && a.sub_assessments.length > 0) {
          a.sub_assessments.forEach(sa => {
            options.push({
              value: sa.id,
              label: `${a.assessment_name} › ${sa.sub_assessment_name}`,
              subjects: sa.subjects || [],
              isSubAssessment: true,
              parentAssessmentId: a.id,
            })
          })
        } else {
          options.push({
            value: a.id,
            label: a.assessment_name,
            subjects: a.subjects || [],
            isSubAssessment: false,
            parentAssessmentId: a.id,
          })
        }
      })
      setAssessmentOptions(options)
    } else {
      setAssessmentOptions([])
    }
  }

  // ── Assessment / Sub-assessment change ────────────────────────────────
  const handleAssessmentChange = option => {
    setSelectedAssessment(option)
    setSelectedCategory(null)
    setSelectedClass(null)
    setSelectedAttempt(null)
    setSelectedSubject(null)
    setClassList([])
    setSubjectOptions([])
    resetReportState()

    if (option) {
      const subjects = option.subjects || []
      setSelectedLevelSubjects(subjects)
      setCategoryOptions(buildCategoryOptions(subjects))
    } else {
      setSelectedLevelSubjects([])
      setCategoryOptions([])
    }
  }

  // ── Category change ───────────────────────────────────────────────────
  const handleCategoryChange = option => {
    setSelectedCategory(option)
    setSelectedClass(null)
    setSelectedAttempt(null)
    setSelectedSubject(null)
    setSubjectOptions([])
    resetReportState()

    if (option) {
      setClassList(buildClassOptions(selectedLevelSubjects, option.value))
    } else {
      setClassList([])
    }
  }

  // ── Class change ──────────────────────────────────────────────────────
  const handleClassChange = option => {
    setSelectedClass(option)
    setSelectedAttempt(null)
    setSelectedSubject(null)
    resetReportState()

    if (option && selectedAssessment && selectedCategory) {
      setSubjectOptions(
        buildSubjectOptions(selectedAssessment, selectedCategory.value)
      )
    } else {
      setSubjectOptions([])
    }
  }

  // ── Attempt change ────────────────────────────────────────────────────
  const handleAttemptChange = option => {
    setSelectedAttempt(option)
    resetReportState()
  }

  // ── Subject change ────────────────────────────────────────────────────
  const handleSubjectChange = option => {
    setSelectedSubject(option)
    resetReportState()
  }

  // ── Fetch mark entries ────────────────────────────────────────────────
  const fetchMarkEntries = async (page = 1, search = "") => {
    if (
      !activeSessionId ||
      !selectedExam ||
      !selectedAssessment ||
      !selectedCategory ||
      !selectedClass ||
      !selectedAttempt
    )
      return

    setIsLoading(true)
    try {
      const payload = {
        session_id: activeSessionId,
        exam_id: selectedExam.value,
        assessment_id: selectedAssessment.isSubAssessment
          ? selectedAssessment.parentAssessmentId
          : selectedAssessment.value,
        ...(selectedAssessment.isSubAssessment && {
          sub_assessment_id: selectedAssessment.value,
        }),
        category_id: selectedCategory.value,
        st_class: selectedClass.st_class,
        st_sec: selectedClass.st_sec,
        attempt: selectedAttempt.value,
        subject_code: selectedSubject?.subjectData?.subject_code || "",
        search: search.trim(),
        page,
      }

      const res = await studentMarkEntries.getMarkEntries(payload)

      if (res?.data?.status === 200 && Array.isArray(res.data.entries)) {
        setRawEntries(res.data.entries)
        setTotalPages(res.data.total_pages ?? 1)
        setTotalCount(res.data.total_count ?? res.data.entries.length)
        setCurrentPage(res.data.page_number ?? page)
        setHasGenerated(true)
      } else {
        Swal.fire(
          "Error",
          res?.data?.msg || "Failed to fetch mark entries.",
          "error"
        )
      }
    } catch (err) {
      console.error("Failed to fetch mark entries:", err.message)
      Swal.fire(
        "Error",
        "Failed to fetch mark entries. Please try again.",
        "error"
      )
    } finally {
      setIsLoading(false)
    }
  }

  // ── Validate & generate ───────────────────────────────────────────────
  const handleGenerateReport = async () => {
    if (!selectedExam)
      return Swal.fire("Validation", "Please select an Examination.", "warning")
    if (!selectedAssessment)
      return Swal.fire("Validation", "Please select an Assessment.", "warning")
    if (!selectedCategory)
      return Swal.fire("Validation", "Please select a Category.", "warning")
    if (!selectedClass)
      return Swal.fire(
        "Validation",
        "Please select a Class / Section.",
        "warning"
      )
    if (!selectedAttempt)
      return Swal.fire("Validation", "Please select an Attempt.", "warning")

    setAppliedSearch("")
    setIsGenerating(true)
    await fetchMarkEntries(1, "")
    setIsGenerating(false)
  }

  // ── Reset ─────────────────────────────────────────────────────────────
  const handleReset = () => {
    setSelectedExam(null)
    setSelectedAssessment(null)
    setSelectedCategory(null)
    setSelectedClass(null)
    setSelectedAttempt(null)
    setSelectedSubject(null)
    setAssessmentOptions([])
    setCategoryOptions([])
    setClassList([])
    setSubjectOptions([])
    setSelectedLevelSubjects([])
    resetReportState()
    setSearchQuery("")
    setAppliedSearch("")
  }

  useEffect(() => {
    if (!hasGenerated) return
    if (searchQuery === appliedSearch) return

    const delayDebounceFn = setTimeout(() => {
      setAppliedSearch(searchQuery)
      fetchMarkEntries(1, searchQuery)
    }, 500)

    return () => clearTimeout(delayDebounceFn)
  }, [searchQuery, hasGenerated, appliedSearch])


  // ── Search ────────────────────────────────────────────────────────────
  const handleSearch = () => {
    if (!hasGenerated) return

    setAppliedSearch(searchQuery)
    fetchMarkEntries(1, searchQuery)
  }

  const handleSearchKeyDown = e => {
    if (e.key === "Enter") handleSearch()
  }

  // ── Pagination ────────────────────────────────────────────────────────
  const handlePageChange = page => {
    if (page < 1 || page > totalPages) return
    fetchMarkEntries(page, searchQuery)
  }

  // ── Derived data ──────────────────────────────────────────────────────
  const reportData = useMemo(() => pivotEntries(rawEntries), [rawEntries])
  const subjectColumns = useMemo(() => {
    const cols = getSubjectColumns(reportData)
    if (!selectedSubject) return cols

    const sData = selectedSubject.subjectData
    const targetLabel = sData?.topic_name
      ? `${sData.subject_name} - ${sData.topic_name}`
      : sData?.subject_name || ""

    return cols.filter(col => col === targetLabel)
  }, [reportData, selectedSubject])

  const filteredReportData = useMemo(() => {
    const q = appliedSearch.trim().toLowerCase()
    if (!q) return reportData
    return reportData.filter(
      row =>
        row.roll_no?.toLowerCase().includes(q) ||
        row.student_name?.toLowerCase().includes(q)
    )
  }, [reportData, appliedSearch])

  // ── Filter summary for exports ────────────────────────────────────────
  const filterSummary = {
    examination: selectedExam?.label || "—",
    assessment: selectedAssessment?.label || "—",
    category: selectedCategory?.label || "—",
    class: selectedClass?.label || "—",
    attempt: selectedAttempt?.label || "—",
  }

  // ── Export Report ──────────────────────────────────────────────────────
  const handleExport = async (format) => {
    if (!activeSessionId || !selectedExam || !selectedAssessment || !selectedCategory || !selectedClass || !selectedAttempt) {
      Swal.fire("Validation", "Please select all required filters.", "warning")
      return
    }

    setIsLoading(true)
    try {
      const payload = {
        session_id: activeSessionId,
        exam_id: selectedExam.value,
        assessment_id: selectedAssessment.isSubAssessment
          ? selectedAssessment.parentAssessmentId
          : selectedAssessment.value,
        ...(selectedAssessment.isSubAssessment && {
          sub_assessment_id: selectedAssessment.value,
        }),
        category_id: selectedCategory.value,
        st_class: selectedClass.st_class,
        st_sec: selectedClass.st_sec,
        attempt: selectedAttempt.value,
        export_format: format,
      }

      const res = await studentMarkEntries.exportMarkReport(payload)
      
      // If the response is JSON (usually an error message), don't download it
      if (res.data && res.data.type === "application/json") {
        const text = res.data.text ? await res.data.text() : await new Promise(r => {
          const reader = new FileReader()
          reader.onload = () => r(reader.result)
          reader.readAsText(res.data)
        })
        try {
          const errorJson = JSON.parse(text)
          Swal.fire("Error", errorJson.msg || "Failed to export report.", "error")
        } catch (e) {
          Swal.fire("Error", "Failed to export report.", "error")
        }
        return
      }

      const fileType = format === "excel" 
        ? "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" 
        : "application/pdf"
      const fileExtension = format === "excel" ? "xlsx" : "pdf"
      
      const blob = new Blob([res.data], { type: fileType })
      const url = window.URL.createObjectURL(blob)
      const link = document.createElement("a")
      link.href = url
      link.setAttribute("download", `Student_Marks_Report.${fileExtension}`)
      document.body.appendChild(link)
      link.click()
      link.remove()
      window.URL.revokeObjectURL(url)
    } catch (err) {
      console.error("Export failed:", err)
      if (err.response?.data && err.response.data instanceof Blob && err.response.data.type === "application/json") {
        try {
          const text = err.response.data.text ? await err.response.data.text() : await new Promise(r => {
            const reader = new FileReader()
            reader.onload = () => r(reader.result)
            reader.readAsText(err.response.data)
          })
          const errorJson = JSON.parse(text)
          Swal.fire("Error", errorJson.msg || "Failed to export report.", "error")
          return
        } catch (e) {
          // fallback
        }
      }
      Swal.fire("Error", "Failed to export report.", "error")
    } finally {
      setIsLoading(false)
    }
  }

  const handleExportExcel = () => handleExport("excel")
  const handleExportPDF = () => handleExport("pdf")

  // ── Pagination controls ───────────────────────────────────────────────
  const renderPagination = () => {
    if (!hasGenerated || totalPages <= 1) return null

    const pages = []
    const maxVisible = 5
    let start = Math.max(1, currentPage - Math.floor(maxVisible / 2))
    let end = Math.min(totalPages, start + maxVisible - 1)
    if (end - start < maxVisible - 1) start = Math.max(1, end - maxVisible + 1)
    for (let i = start; i <= end; i++) pages.push(i)

    return (
      <div className="d-flex justify-content-between align-items-center mt-3">
        <small className="text-muted">
          Showing page <strong>{currentPage}</strong> of{" "}
          <strong>{totalPages}</strong> &nbsp;|&nbsp; Total:{" "}
          <strong>{totalCount}</strong> students
        </small>
        <ul className="pagination pagination-sm mb-0">
          <li className={`page-item ${currentPage === 1 ? "disabled" : ""}`}>
            <button className="page-link" onClick={() => handlePageChange(1)}>
              «
            </button>
          </li>
          <li className={`page-item ${currentPage === 1 ? "disabled" : ""}`}>
            <button
              className="page-link"
              onClick={() => handlePageChange(currentPage - 1)}
            >
              ‹
            </button>
          </li>
          {pages.map(p => (
            <li
              key={p}
              className={`page-item ${p === currentPage ? "active" : ""}`}
            >
              <button className="page-link" onClick={() => handlePageChange(p)}>
                {p}
              </button>
            </li>
          ))}
          <li
            className={`page-item ${
              currentPage === totalPages ? "disabled" : ""
            }`}
          >
            <button
              className="page-link"
              onClick={() => handlePageChange(currentPage + 1)}
            >
              ›
            </button>
          </li>
          <li
            className={`page-item ${
              currentPage === totalPages ? "disabled" : ""
            }`}
          >
            <button
              className="page-link"
              onClick={() => handlePageChange(totalPages)}
            >
              »
            </button>
          </li>
        </ul>
      </div>
    )
  }

  // ── Table body ────────────────────────────────────────────────────────
  const renderTableBody = () => {
    if (!hasGenerated && !isLoading) {
      return (
        <tr>
          <td
            colSpan={2 + subjectColumns.length || 5}
            className="text-center py-5 text-muted"
          >
            <i
              className="bx bx-bar-chart-alt-2"
              style={{
                fontSize: "2rem",
                display: "block",
                marginBottom: "8px",
              }}
            />
            Select all filters and click <strong>Generate Report</strong> to
            view marks.
          </td>
        </tr>
      )
    }

    if (isLoading) {
      return (
        <tr>
          <td
            colSpan={2 + subjectColumns.length || 5}
            className="text-center py-5 text-muted"
          >
            <i
              className="bx bx-loader-alt bx-spin"
              style={{
                fontSize: "2rem",
                display: "block",
                marginBottom: "8px",
              }}
            />
            Loading marks...
          </td>
        </tr>
      )
    }

    if (filteredReportData.length === 0) {
      return (
        <tr>
          <td
            colSpan={2 + subjectColumns.length || 5}
            className="text-center py-5 text-muted"
          >
            <i
              className="bx bx-search-alt"
              style={{
                fontSize: "2rem",
                display: "block",
                marginBottom: "8px",
              }}
            />
            {searchQuery
              ? "No students match your search."
              : "No Mark Entry Records Found."}
          </td>
        </tr>
      )
    }

    return filteredReportData.map(student => (
      <tr key={student.id} className="text-center">
        <td>{student.roll_no}</td>
        <td className="text-start">{student.student_name}</td>
        {subjectColumns.map(subject => (
          <td key={subject} className="text-start font-semibold">
            {student.subjects[subject] !== undefined ? (
              student.subjects[subject]
            ) : (
              <span className="text-muted text-start">—</span>
            )}
          </td>
        ))}
      </tr>
    ))
  }

  return (
    <React.Fragment>
      <div className="page-content">
        <div className="container-fluid">
          <Container fluid={true}>
            <Breadcrumb
              title="Examination"
              breadcrumbItem="Student Marks Report"
            />

            {/* ── Filter Section ──────────────────────────────────────── */}
            <div className="p-3 mb-4 rounded bg-white">
              <Row>
                {/* Examination */}
                <Col lg={3} md={6} className="mb-3">
                  <label className="form-label fw-semibold mb-1">
                    Examination <span className="text-danger">*</span>
                  </label>
                  <Select
                    name="st_exam"
                    classNamePrefix="select2-selection"
                    placeholder="Select Examination..."
                    options={examOptions}
                    value={selectedExam}
                    onChange={handleExamChange}
                    onMenuScrollToBottom={handleExamMenuScrollToBottom}
                    isLoading={examsLoadingMore}
                    styles={selectStyles}
                  />
                </Col>

                {/* Assessment / Sub-assessment */}
                <Col lg={3} md={6} className="mb-3">
                  <label className="form-label fw-semibold mb-1">
                    Assessment <span className="text-danger">*</span>
                  </label>
                  <Select
                    name="st_assessment"
                    classNamePrefix="select2-selection"
                    placeholder="Select Assessment..."
                    options={assessmentOptions}
                    value={selectedAssessment}
                    onChange={handleAssessmentChange}
                    isDisabled={!selectedExam}
                    styles={selectStyles}
                  />
                </Col>

                {/* Category — derived from selected assessment's subjects */}
                <Col lg={2} md={6} className="mb-3">
                  <label className="form-label fw-semibold mb-1">
                    Category <span className="text-danger">*</span>
                  </label>
                  <Select
                    name="st_category"
                    classNamePrefix="select2-selection"
                    placeholder="Select Category..."
                    options={categoryOptions}
                    value={selectedCategory}
                    onChange={handleCategoryChange}
                    isDisabled={!selectedAssessment}
                    styles={selectStyles}
                  />
                </Col>

                {/* Class — derived from selected category's subjects */}
                <Col lg={2} md={6} className="mb-3">
                  <label className="form-label fw-semibold mb-1">
                    Class / Section <span className="text-danger">*</span>
                  </label>
                  <Select
                    name="st_class"
                    classNamePrefix="select2-selection"
                    placeholder={
                      !selectedCategory
                        ? "Select a Category first..."
                        : classList.length === 0
                        ? "No classes available"
                        : "Select Class / Section..."
                    }
                    options={classList}
                    value={selectedClass}
                    onChange={handleClassChange}
                    isDisabled={!selectedCategory || classList.length === 0}
                    styles={selectStyles}
                  />
                </Col>

                {/* Attempt */}
                <Col lg={2} md={6} className="mb-3">
                  <label className="form-label fw-semibold mb-1">
                    Attempt <span className="text-danger">*</span>
                  </label>
                  <Select
                    name="attempt"
                    classNamePrefix="select2-selection"
                    placeholder={
                      !selectedClass
                        ? "Select..."
                        : "Select Attempt..."
                    }
                    options={ATTEMPT_OPTIONS}
                    value={selectedAttempt}
                    onChange={handleAttemptChange}
                    isDisabled={!selectedClass}
                    styles={selectStyles}
                  />
                </Col>
              </Row>

              {/* Subject row — shown once Class and Attempt are selected */}
              {selectedClass && selectedAttempt && (
                <Row className="align-items-end mt-1">
                  <Col lg={4} md={6} className="mb-3">
                    <label className="form-label fw-semibold mb-1">
                      Subject{" "}
                      <span
                        className="text-muted fw-normal"
                        style={{ fontSize: "12px" }}
                      >
                        (optional — filter by subject)
                      </span>
                    </label>
                    <Select
                      name="st_subject"
                      classNamePrefix="select2-selection"
                      placeholder={
                        subjectOptions.length === 0
                          ? "No subjects available"
                          : "All subjects (or select one...)"
                      }
                      options={subjectOptions}
                      value={selectedSubject}
                      onChange={handleSubjectChange}
                      isDisabled={subjectOptions.length === 0}
                      styles={selectStyles}
                      isClearable
                    />
                  </Col>
                  <Col className="mb-3">
                    <Button
                      color="primary"
                      onClick={handleGenerateReport}
                      className="me-2"
                      disabled={isLoading || isGenerating}
                    >
                      <i className="bx bx-bar-chart-alt-2 me-1" />
                      {isGenerating ? "Loading..." : "Generate Report"}
                    </Button>
                    <Button
                      color="secondary"
                      outline
                      onClick={handleReset}
                      disabled={isLoading}
                    >
                      <i className="bx bx-reset me-1" />
                      Reset
                    </Button>
                  </Col>
                </Row>
              )}
            </div>

            {/* ── Report Summary ──────────────────────────────────────── */}
            {hasGenerated && (
              <div
                className="p-3 mb-3 rounded d-flex flex-wrap gap-3"
                style={{
                  backgroundColor: "#e8f4fd",
                  border: "1px solid #b8daff",
                  fontSize: "13px",
                }}
              >
                <span>
                  <strong>Examination:</strong>{" "}
                  <span className="text-primary">{selectedExam?.label}</span>
                </span>
                <span className="text-muted">|</span>
                <span>
                  <strong>Assessment:</strong>{" "}
                  <span className="text-primary">
                    {selectedAssessment?.label}
                  </span>
                </span>
                <span className="text-muted">|</span>
                <span>
                  <strong>Category:</strong>{" "}
                  <span className="text-primary">
                    {selectedCategory?.label}
                  </span>
                </span>
                <span className="text-muted">|</span>
                <span>
                  <strong>Class:</strong>{" "}
                  <span className="text-primary">{selectedClass?.label}</span>
                </span>
                {selectedSubject && (
                  <>
                    <span className="text-muted">|</span>
                    <span>
                      <strong>Subject:</strong>{" "}
                      <span className="text-primary">{selectedSubject.label}</span>
                    </span>
                  </>
                )}
                {selectedAttempt && (
                  <>
                    <span className="text-muted">|</span>
                    <span>
                      <strong>Attempt:</strong>{" "}
                      <span className="text-primary">{selectedAttempt.label}</span>
                    </span>
                  </>
                )}
              </div>
            )}

            {/* ── Actions & Search Row ────────────────────────────────── */}
            <Row className="mb-3 align-items-center">
              <Col lg={5} md={12} className="mb-2 mb-lg-0">
                <div className="d-flex gap-2">
                  <div style={{ position: "relative", width: "100%" }}>
                    <Input
                      type="text"
                      placeholder="Search by Student Name or Student Code..."
                      value={searchQuery}
                      onChange={e => setSearchQuery(e.target.value)}
                      onKeyDown={handleSearchKeyDown}
                      disabled={!hasGenerated}
                      style={{ fontSize: "13px", paddingRight: "30px" }}
                    />
                    {searchQuery && (
                      <button
                        type="button"
                        onClick={() => {
                          setSearchQuery("")
                          setAppliedSearch("")
                          fetchMarkEntries(1, "")
                        }}
                        style={{
                          position: "absolute",
                          right: "10px",
                          top: "50%",
                          transform: "translateY(-50%)",
                          border: "none",
                          background: "none",
                          cursor: "pointer",
                          fontSize: "12px",
                          padding: 0,
                          color: "#6c757d",
                          zIndex: 5,
                        }}
                      >
                        ❌
                      </button>
                    )}
                  </div>
                  <Button
                    color="primary"
                    onClick={handleSearch}
                    disabled={!hasGenerated || isLoading}
                    style={{ whiteSpace: "nowrap" }}
                  >
                    <i className="bx bx-search me-1" />
                    Search
                  </Button>
                </div>
              </Col>

              <Col lg={7} md={12}>
                <div className="d-flex gap-2 justify-content-lg-end flex-wrap">
                  <Link to="/mark-entries">
                    <Button color="primary" outline>
                      <i className="bx bx-edit me-1" />
                      <strong>Mark Entry</strong>
                    </Button>
                  </Link>
                  <Dropdown
                    isOpen={exportDropdownOpen}
                    toggle={() => setExportDropdownOpen(prev => !prev)}
                    disabled={!hasGenerated}
                  >
                    <DropdownToggle
                      color="dark"
                      size="md"
                      caret
                      disabled={!hasGenerated}
                    >
                      <i className="bx bx-export me-1" />
                      Export
                    </DropdownToggle>
                    <DropdownMenu end>
                      <DropdownItem onClick={handleExportExcel}>
                        <i className="bx bx-file me-2 text-success" />
                        Export Excel
                      </DropdownItem>
                      <DropdownItem onClick={handleExportPDF}>
                        <i className="bx bxs-file-pdf me-2 text-danger" />
                        Export PDF
                      </DropdownItem>
                    </DropdownMenu>
                  </Dropdown>
                </div>
              </Col>
            </Row>

            {/* ── Report Table ────────────────────────────────────────── */}
            <div className="table-responsive">
              <Table className="table table-striped table-bordered align-middle">
                <thead>
                  <tr className="text-center table-secondary">
                    <th style={{ width: "110px", whiteSpace: "nowrap" }}>
                      Student Code
                    </th>
                    <th
                      className="text-start"
                      style={{ minWidth: "160px", whiteSpace: "nowrap" }}
                    >
                      Student Name
                    </th>
                    {subjectColumns.map(subject => (
                      <th
                        className="text-start"
                        key={subject}
                        style={{ minWidth: "110px" }}
                      >
                        {subject}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>{renderTableBody()}</tbody>
              </Table>
            </div>

            {/* Pagination */}
            {renderPagination()}
          </Container>
        </div>
      </div>
    </React.Fragment>
  )
}

export default StudentMarksReport
